import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inheritsLoose from "@babel/runtime/helpers/inheritsLoose";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import StaticIcon from '../StaticIcon';
import { visuallyHidden } from '../styles';
var Wrapper = styled.div.withConfig({
  displayName: "Checkbox__Wrapper",
  componentId: "sc-1w4c4o-0"
})(["\n  font-size: 14px;\n"]);
var InputWrapper = styled.label.withConfig({
  displayName: "Checkbox__InputWrapper",
  componentId: "sc-1w4c4o-1"
})(["\n  display: ", ";\n  align-items: ", ";\n  color: ", ";\n  vertical-align: top;\n  user-select: none;\n"], function (props) {
  return props.fullWidth ? 'flex' : 'inline-flex';
}, function (props) {
  return props.verticalAlign || 'baseline';
}, function (props) {
  return props.white ? '#fff' : 'inherit';
});
var Label = styled.span.withConfig({
  displayName: "Checkbox__Label",
  componentId: "sc-1w4c4o-2"
})(["\n  flex: 1;\n"]);
var ErrorMessage = styled.p.withConfig({
  displayName: "Checkbox__ErrorMessage",
  componentId: "sc-1w4c4o-3"
})(["\n  margin: 4px 0 0 30px;\n  font-size: 10px;\n  color: #e35d6d;\n"]);
var HiddenInput = styled.input.attrs({
  type: 'checkbox'
}).withConfig({
  displayName: "Checkbox__HiddenInput",
  componentId: "sc-1w4c4o-4"
})(["\n  ", ";\n"], visuallyHidden);
var CustomCheckbox = styled.span.withConfig({
  displayName: "Checkbox__CustomCheckbox",
  componentId: "sc-1w4c4o-5"
})(["\n  display: inline-block;\n  box-sizing: border-box;\n  width: ", "px;\n  height: ", "px;\n  border: 2px solid\n    ", ";\n  margin-right: 10px;\n  padding-top: ", "px;\n  /* We want the check icon itself to align with the baseline of text, so the\n   * line-height needs to reflect where the check icon will be. */\n  line-height: 13px;\n  background-color: ", ";\n  color: #000;\n  text-align: center;\n\n  > svg {\n    /* Fix for Firefox and Safari issue where changing visibility drops clicks */\n    pointer-events: none;\n    visibility: hidden;\n    vertical-align: top !important;\n  }\n\n  input:checked + & {\n    background-color: #fff;\n\n    > svg {\n      visibility: visible;\n    }\n  }\n\n  input:active + & {\n    border-color: ", ";\n\n    > svg {\n      visibility: visible;\n      opacity: 0.2;\n    }\n  }\n\n  input:checked:active + & {\n    > svg {\n      opacity: 0.7;\n    }\n  }\n\n  input:focus + & {\n    /* Since the real input is hidden, it won't render an outline when focused.\n       We need some focus state for accessibility. */\n    border-color: ", ";\n  }\n\n  ", ";\n"], function (props) {
  return props.size === 'small' ? 20 : 24;
}, function (props) {
  return props.size === 'small' ? 20 : 24;
}, function (props) {
  return props.error ? '#e35d6d' : props.white ? '#fff' : '#000';
}, function (props) {
  return props.size === 'small' ? 2 : 4;
}, function (props) {
  return props.white ? 'transparent' : '#fff';
}, function (props) {
  return props.theme.focusColor || '#7c89d3';
}, function (props) {
  return props.theme.focusColor || '#7c89d3';
}, function (props) {
  return props.theme.checkbox ? props.theme.checkbox.defaultStyle : '';
});
/** @component
 * A default checkbox and helper for creating custom checkboxes that are backed
 * by a real `<input type="checkbox">` element. This allows the custom checkbox
 * to inherit all the same accessibility and input device handling as a real
 * checkbox. (Even if you use a bunch of ARIA attributes to make a checkbox
 * control accessible, you'd still have to reproduce much of the keyboard and
 * mouse interactions manually. That's why we do it this way.)
 *
 * Any props beyond those documented below will be passed along to the
 * underlying `<input>` element.
 *
 * ### Styling with pseudo-classes
 *
 * Your checkbox CSS should use a [sibling combinator](https://developer.mozilla.org/en-US/docs/Web/CSS/Adjacent_sibling_selectors)
 * to target the custom checkbox using pseudo-classes on the real `<input>`
 * element:
 *
 * ```css
 *
 * input:hover + & {
 *   background: yellow;
 * }
 *
 * input:checked + & {
 *   border-color: black;
 * }
 *
 * input:focus + & {
 *   border-color: blue;
 * }
 *
 * input:active + & {
 *   background: gray;
 * }
 * ```
 *
 * Additionally, the following subcomponents are available (for rendering
 * yourself or in CSS selectors):
 *
 * * `Checkbox.InputWrapper`: The `<label>` element that wraps both the
 *   backing `<input>` element and the `label` you provide.
 * * `Checkbox.HiddenInput`: The hidden checkbox `<input>` element backing the
 *   custom checkbox.
 * * `Checkbox.Label`: The element containing the given `label`, if present.
 * * `Checkbox.ErrorMessage`: The element containing the given `error`, if
 *   present.
 */

var Checkbox =
/*#__PURE__*/
function (_React$PureComponent) {
  _inheritsLoose(Checkbox, _React$PureComponent);

  function Checkbox() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$PureComponent.call.apply(_React$PureComponent, [this].concat(args)) || this;

    _defineProperty(_assertThisInitialized(_this), "handleChange", function (event) {
      if (_this.props.onChange) {
        _this.props.onChange(event.target.checked, _this.props.name);
      }
    });

    return _this;
  }

  var _proto = Checkbox.prototype;

  _proto.render = function render() {
    var _this$props = this.props,
        className = _this$props.className,
        label = _this$props.label,
        error = _this$props.error,
        onChange = _this$props.onChange,
        white = _this$props.white,
        fullWidth = _this$props.fullWidth,
        size = _this$props.size,
        verticalAlign = _this$props.verticalAlign,
        inputProps = _objectWithoutPropertiesLoose(_this$props, ["className", "label", "error", "onChange", "white", "fullWidth", "size", "verticalAlign"]);

    var customProps = _objectSpread({
      error: error,
      white: white,
      fullWidth: fullWidth,
      size: size,
      verticalAlign: verticalAlign
    }, inputProps);

    return React.createElement(Wrapper, {
      className: className
    }, React.createElement(InputWrapper, {
      white: white,
      fullWidth: fullWidth,
      verticalAlign: verticalAlign
    }, React.createElement(HiddenInput, _extends({}, inputProps, {
      onChange: this.handleChange
    })), React.createElement(CustomCheckbox, customProps, React.createElement(StaticIcon, {
      type: "check",
      size: 12,
      role: "presentation"
    })), label ? React.createElement(Label, null, label) : null), error && error !== true ? React.createElement(ErrorMessage, null, error) : null);
  };

  return Checkbox;
}(React.PureComponent);

_defineProperty(Checkbox, "Wrapper", Wrapper);

_defineProperty(Checkbox, "InputWrapper", InputWrapper);

_defineProperty(Checkbox, "HiddenInput", HiddenInput);

_defineProperty(Checkbox, "CustomCheckbox", CustomCheckbox);

_defineProperty(Checkbox, "Label", Label);

_defineProperty(Checkbox, "ErrorMessage", ErrorMessage);

_defineProperty(Checkbox, "propTypes", {
  /**
   * Class name(s) to pass to the custom checkbox element provided by the
   * `render` prop. This allows you to style a custom checkbox using
   * `styled(Checkbox)`.
   */
  className: PropTypes.string,

  /**
   * The input name.
   */
  name: PropTypes.string,

  /**
   * The input value. This is the value `onChange` will be called with when
   * the checkbox button is selected.
   */
  value: PropTypes.string,

  /**
   * The label to render next to the checkbox. It can be anything renderable,
   * including React elements. Note that it will already be wrapped in a
   * `<label>` element, so you shouldn't supply one!
   */
  label: PropTypes.node,

  /**
   * An error message to render below the label, or a Boolean indicating
   * whether to render the error state (with no message).
   */
  error: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),

  /**
   * Whether to render light text for use on a dark background.
   */
  white: PropTypes.bool,

  /**
   * Whether the `InputWrapper` element should extend to the full width of the
   * container so that clicking anywere in it selects the option (since it
   * renders a `<label>`. Otherwise, only clicking the input and label text
   * itself will select it.
   */
  fullWidth: PropTypes.bool,

  /**
   * What size checkbox to render. Defaults to small.
   */
  size: PropTypes.oneOf(['small', 'large']),

  /**
   * How to align the checkbox with its label, in flexbox terminology.
   */
  verticalAlign: PropTypes.string,

  /**
   * The function to call when the checkbox state changes. It will be called
   * with the `checked` value (true or false) and the input `name`.
   */
  onChange: PropTypes.func
});

_defineProperty(Checkbox, "defaultProps", {
  white: false,
  fullWidth: false,
  size: 'small',
  verticalAlign: 'baseline'
});

export { Checkbox as default };